const AMOUNT_FMT = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
});

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

export function formatAmount(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount == null) return '—';
  if (currency && currency.toUpperCase() !== 'EUR') {
    try {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }
  return AMOUNT_FMT.format(amount);
}

export function formatInvoiceDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return DATE_FMT.format(new Date(iso));
}

const REL_FMT = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
const DAY = 86_400_000;

export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = then - now.getTime();
  const days = Math.round(diff / DAY);
  if (Math.abs(days) >= 1) return REL_FMT.format(days, 'day');
  const hours = Math.round(diff / 3_600_000);
  if (Math.abs(hours) >= 1) return REL_FMT.format(hours, 'hour');
  const minutes = Math.round(diff / 60_000);
  return REL_FMT.format(minutes, 'minute');
}

export type DueChipTone = 'overdue' | 'today' | 'soon' | 'later' | 'unknown';

export interface DueChip {
  label: string;
  tone: DueChipTone;
}

// REQ-FE-031: mockup `03-invoice-list.png` shows a compact due-date
// chip on every invoice row — "Dépassée 1 j" (red), "Aujourd'hui"
// (green), "J-2" / "J-4" (muted). This helper centralises the FR
// labels + the tone selection so InvoiceRow + the standalone detail
// header can share it.
//
// REQ-FE-037 item 10: when the due date was computed as the LME-60
// fallback rather than supplied by the structured invoice, the chip
// still renders (no skip) and the label gets an " EST." suffix per
// mockup `03-invoice-list.png` row 4 — `Dépassée 1 j EST.` / `J-50 EST.`.
export function formatDueChip(
  iso: string | null | undefined,
  now: Date = new Date(),
  isEstimated = false,
): DueChip {
  if (!iso) return { label: '—', tone: 'unknown' };
  const due = new Date(iso).getTime();
  if (Number.isNaN(due)) return { label: '—', tone: 'unknown' };
  // Normalise to midnight on both sides so partial-day clock drift
  // doesn't flip the bucket.
  const midnightNow = new Date(now);
  midnightNow.setHours(0, 0, 0, 0);
  const midnightDue = new Date(due);
  midnightDue.setHours(0, 0, 0, 0);
  const days = Math.round((midnightDue.getTime() - midnightNow.getTime()) / DAY);
  const suffix = isEstimated ? ' EST.' : '';
  if (days < 0) {
    const n = Math.abs(days);
    return { label: `Dépassée ${n} j${suffix}`, tone: 'overdue' };
  }
  if (days === 0) return { label: `Aujourd'hui${suffix}`, tone: 'today' };
  if (days <= 3) return { label: `J-${days}${suffix}`, tone: 'soon' };
  return { label: `J-${days}${suffix}`, tone: 'later' };
}
